import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2, Sparkles, Bot, ExternalLink, Briefcase, MessageSquare, FileText, Settings, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import { getEmojiFromCode } from '@/pages/Assessor/Settings';
import { JurisprudenciaDetailDialog } from '@/components/JurisprudenciaDetailDialog';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources: Source[];
  created_at: string;
}

interface Source {
  resource_type: string;
  resource_id: string;
  title: string;
  snippet: string;
}

interface FloatingAssessorChatProps {
  position: { x: number; y: number };
  onClose: () => void;
  assistantName: string;
  assistantAvatar: string | null;
}

export const FloatingAssessorChat: React.FC<FloatingAssessorChatProps> = ({
  position,
  onClose,
  assistantName,
  assistantAvatar,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [selectedJurisprudenciaId, setSelectedJurisprudenciaId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Calculate position for the chat window
  const chatStyle = React.useMemo(() => {
    const chatW = 380;
    const chatH = 500;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = position.x - chatW - 16;
    let top = position.y - chatH / 2 + 28;

    if (left < 8) left = position.x + 72;
    if (left + chatW > vw - 8) left = vw - chatW - 8;
    if (top < 8) top = 8;
    if (top + chatH > vh - 8) top = vh - chatH - 8;

    // Mobile: full screen above bottom nav
    if (vw < 640) {
      const mobileBottomInset = 'max(80px, calc(70px + env(safe-area-inset-bottom)))';
      return {
        left: 0,
        top: 0,
        right: 0,
        bottom: mobileBottomInset,
        width: '100vw',
        borderRadius: 0,
      };
    }

    return { left, top, width: chatW, height: chatH };
  }, [position]);

  const renderAvatar = (size: 'sm' | 'lg' = 'sm') => {
    const sizeClass = size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
    const textSize = size === 'lg' ? 'text-lg' : 'text-sm';
    const iconSize = size === 'lg' ? 'h-4 w-4' : 'h-3 w-3';
    return (
      <div className={`${sizeClass} rounded-full overflow-hidden flex items-center justify-center shrink-0`}>
        {assistantAvatar?.startsWith('emoji:') ? (
          <div className={`w-full h-full bg-primary/10 flex items-center justify-center ${textSize}`}>
            {getEmojiFromCode(assistantAvatar)}
          </div>
        ) : assistantAvatar ? (
          <img src={assistantAvatar} alt="Avatar" className="w-full h-full object-cover rounded-full" />
        ) : (
          <div className="w-full h-full bg-primary/10 flex items-center justify-center">
            <Sparkles className={`${iconSize} text-primary`} />
          </div>
        )}
      </div>
    );
  };

  const handleSend = async () => {
    if (!inputMessage.trim() || isSending) return;
    const userMsg = inputMessage.trim();
    setInputMessage('');
    setIsSending(true);

    const tempUser: Message = {
      id: 'temp-user',
      role: 'user',
      content: userMsg,
      sources: [],
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUser]);

    try {
      const response = await supabase.functions.invoke('assistant-chat', {
        body: { thread_id: threadId, message: userMsg },
      });

      if (response.error) throw new Error(response.error.message || 'Erro ao enviar');

      const data = response.data;
      if (!threadId && data.thread_id) setThreadId(data.thread_id);

      const assistantMsg: Message = {
        id: 'asst-' + Date.now(),
        role: 'assistant',
        content: data.response,
        sources: data.sources || [],
        created_at: new Date().toISOString(),
      };

      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== 'temp-user');
        return [...filtered, { ...tempUser, id: 'user-' + Date.now() }, assistantMsg];
      });
    } catch (error: any) {
      toast.error(error.message || 'Erro ao enviar mensagem');
      setMessages(prev => prev.filter(m => m.id !== 'temp-user'));
      setInputMessage(userMsg);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const navigateToSource = (source: Source) => {
    if (source.resource_type === 'JURISPRUDENCIA') {
      setSelectedJurisprudenciaId(source.resource_id);
      return;
    }
    switch (source.resource_type) {
      case 'CASO':
      case 'CASO_DOCUMENT':
        navigate(`/casos/${source.resource_id}`);
        break;
      case 'LEGAL_CHAT':
        navigate(`/chat-juridico/${source.resource_id}`);
        break;
    }
    onClose();
  };

  const getResourceIcon = (type: string) => {
    switch (type) {
      case 'CASO':
      case 'CASO_DOCUMENT':
        return <Briefcase className="h-3 w-3" />;
      case 'LEGAL_CHAT':
        return <MessageSquare className="h-3 w-3" />;
      case 'JURISPRUDENCIA':
        return <Scale className="h-3 w-3" />;
      default:
        return <FileText className="h-3 w-3" />;
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 10000,
        ...chatStyle,
      }}
      className="bg-background border border-border rounded-2xl shadow-2xl flex min-h-0 flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-card/50 shrink-0">
        <div className="flex items-center gap-2">
          {renderAvatar('lg')}
          <div>
            <h3 className="text-sm font-semibold">{assistantName}</h3>
            <p className="text-[10px] text-muted-foreground">Seu assistente pessoal</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => navigate('/assessor')}
            title="Abrir completo"
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center space-y-3 px-4">
              <Bot className="h-10 w-10 text-muted-foreground mx-auto" />
              <div>
                <h4 className="text-sm font-medium">Como posso ajudar?</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Pergunte sobre seus casos, documentos ou conversas.
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5 justify-center">
                {['Resumo dos meus casos', 'Quais documentos tenho?'].map(s => (
                  <Button
                    key={s}
                    variant="outline"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setInputMessage(s)}
                  >
                    {s}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map(message => (
              <div
                key={message.id}
                className={cn('flex gap-2', message.role === 'user' && 'justify-end')}
              >
                {message.role === 'assistant' && renderAvatar('sm')}
                <div
                  className={cn(
                    'max-w-[85%] rounded-xl px-3 py-2 text-sm',
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-sm'
                      : 'bg-muted rounded-bl-sm'
                  )}
                >
                  <div className="prose prose-sm dark:prose-invert max-w-none text-xs">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                  {message.sources?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border/50">
                      <div className="flex flex-wrap gap-1">
                        {message.sources.map((source, i) => (
                          <button
                            key={i}
                            onClick={() => navigateToSource(source)}
                            className="inline-flex items-center gap-1 text-[10px] bg-background/50 hover:bg-background px-1.5 py-0.5 rounded-full transition-colors"
                          >
                            {getResourceIcon(source.resource_type)}
                            <span className="truncate max-w-[100px]">{source.title}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isSending && (
              <div className="flex gap-2">
                {renderAvatar('sm')}
                <div className="bg-muted rounded-xl rounded-bl-sm px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span className="text-xs text-muted-foreground">Pesquisando...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t p-3 bg-card/30 shrink-0">
        <div className="flex gap-2">
          <Input
            value={inputMessage}
            onChange={e => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Pergunte algo..."
            disabled={isSending}
            className="flex-1 rounded-xl text-sm h-9"
          />
          <Button
            onClick={handleSend}
            disabled={!inputMessage.trim() || isSending}
            size="icon"
            className="rounded-xl h-9 w-9"
          >
            {isSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
      <JurisprudenciaDetailDialog
        jurisprudenciaId={selectedJurisprudenciaId}
        open={!!selectedJurisprudenciaId}
        onOpenChange={(open) => { if (!open) setSelectedJurisprudenciaId(null); }}
        chatMode
      />
    </div>
  );
};